import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Bell, CircleNotch, CheckCircle, BellRinging } from '@phosphor-icons/react';
import { API } from '@/config/api';
import { useNotificationsSocket } from '@/hooks/useNotificationsSocket';

/* --------------------------------------------------------------------------
 * NotificationBell — real-time bell inbox for recruiters and institutions.
 *
 * Props
 * -----
 * - basePath: '/recruiter' | '/institution' (defaults to '/recruiter')
 *
 * Behaviour
 * ---------
 * - Fetches the last 30 notifications on mount and every 60s (fallback).
 * - Opens a WebSocket to /api/ws/notifications and prepends any push events
 *   the moment they arrive.
 * - Shows a browser Notification if the user granted permission AND the
 *   tab is hidden (Page Visibility API).
 * - Click bell → dropdown with color-coded kind pills + relative times.
 * - Click a notification → marks it read. "Mark all as read" bulk button.
 * ------------------------------------------------------------------------- */

const KIND_META = {
  stage_change:     { color: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  missing_doc:      { color: 'bg-amber-100 text-amber-800 border-amber-200' },
  student_arrival:  { color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  doc_resolved:     { color: 'bg-blue-100 text-blue-800 border-blue-200' },
};

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  const days = Math.floor(s / 86400);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

export default function NotificationBell({ basePath = '/recruiter' }) {
  const testIdPrefix = basePath.startsWith('/institution') ? 'institution' : 'recruiter';
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [browserPermission, setBrowserPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const dropdownRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}${basePath}/notifications?limit=30`);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setUnread(data?.unread_count || 0);
    } catch {
      /* silent */
    } finally { setLoading(false); }
  }, [basePath]);

  useEffect(() => { load(); }, [load]);

  // Fallback poll every 60s in case the WS is down.
  useEffect(() => {
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  // Realtime push handler — prepend new notifications when they arrive.
  const handlePush = useCallback((payload) => {
    if (!payload || payload.type !== 'notification' || !payload.notification) return;
    const n = payload.notification;
    setItems(prev => {
      if (prev.some(x => x.id === n.id)) return prev;
      return [n, ...prev].slice(0, 60);
    });
    setUnread(prev => prev + 1);
    // Browser toast when tab is hidden.
    try {
      if (typeof Notification !== 'undefined'
          && Notification.permission === 'granted'
          && document.hidden) {
        const notif = new Notification(n.title || 'New update', {
          body: n.message || '',
          icon: '/logo.png',
          tag: n.id,
        });
        // Focus the tab when the user clicks.
        notif.onclick = () => { window.focus(); notif.close(); };
      }
    } catch { /* ignore */ }
  }, []);

  useNotificationsSocket(handlePush);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}${basePath}/notifications/${id}/read`);
      setItems(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnread(prev => Math.max(0, prev - 1));
    } catch { /* ignore */ }
  };

  const markAll = async () => {
    try {
      await axios.post(`${API}${basePath}/notifications/mark-all-read`);
      setItems(prev => prev.map(n => ({ ...n, read: true })));
      setUnread(0);
    } catch { /* ignore */ }
  };

  const requestBrowserPerm = async () => {
    if (typeof Notification === 'undefined') return;
    try {
      const p = await Notification.requestPermission();
      setBrowserPermission(p);
    } catch { /* ignore */ }
  };

  return (
    <div className="relative" ref={dropdownRef} data-testid={`${testIdPrefix}-notification-bell`}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="relative w-10 h-10 rounded-full bg-white/70 backdrop-blur border border-gray-200 hover:border-blue-300 hover:bg-white flex items-center justify-center transition-colors shadow-sm"
        aria-label="Notifications"
        data-testid={`${testIdPrefix}-bell-btn`}
      >
        <Bell size={17} weight={unread > 0 ? 'fill' : 'regular'} className={unread > 0 ? 'text-blue-700' : 'text-gray-600'} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full animate-pulse" data-testid={`${testIdPrefix}-bell-badge`}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-96 max-w-[92vw] bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden z-40" data-testid={`${testIdPrefix}-bell-dropdown`}>
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
            <p className="text-sm font-semibold text-gray-900">Notifications</p>
            {unread > 0 && (
              <button onClick={markAll} className="text-[11px] text-blue-700 hover:underline font-medium" data-testid={`${testIdPrefix}-bell-mark-all-read-btn`}>
                Mark all as read
              </button>
            )}
          </div>
          {browserPermission === 'default' && (
            <div className="px-4 py-2.5 bg-blue-50 border-b border-blue-100 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <BellRinging size={14} className="text-blue-700 shrink-0" />
                <p className="text-[11px] text-blue-900 truncate">Turn on browser notifications for instant alerts.</p>
              </div>
              <button onClick={requestBrowserPerm} className="text-[11px] text-blue-700 hover:underline font-medium whitespace-nowrap shrink-0" data-testid={`${testIdPrefix}-bell-enable-browser-btn`}>
                Enable
              </button>
            </div>
          )}
          <div className="max-h-96 overflow-y-auto">
            {loading && items.length === 0 && (
              <div className="p-10 text-center text-gray-400">
                <CircleNotch size={20} className="animate-spin mx-auto mb-2" />
                <p className="text-xs">Loading…</p>
              </div>
            )}
            {!loading && items.length === 0 && (
              <div className="p-10 text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-2">
                  <CheckCircle size={20} className="text-gray-400" />
                </div>
                <p className="text-sm font-medium text-gray-700">You&rsquo;re all caught up</p>
                <p className="text-xs text-gray-500 mt-0.5">New updates land here instantly.</p>
              </div>
            )}
            {items.map(n => {
              const meta = KIND_META[n.kind] || { color: 'bg-gray-100 text-gray-700 border-gray-200' };
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => !n.read && markRead(n.id)}
                  className={`w-full text-left p-3.5 border-b border-gray-100 last:border-b-0 transition-colors ${n.read ? 'bg-white hover:bg-gray-50' : 'bg-blue-50/50 hover:bg-blue-50'}`}
                  data-testid={`${testIdPrefix}-bell-notification-${n.id}`}
                >
                  <div className="flex items-start gap-2.5">
                    {!n.read && <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-600 shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider border ${meta.color}`}>
                          {(n.kind || '').replace(/_/g, ' ')}
                        </span>
                        <p className={`text-sm font-semibold ${n.read ? 'text-gray-700' : 'text-gray-900'} truncate`}>{n.title}</p>
                      </div>
                      {n.message && <p className="text-xs text-gray-600 mt-1 leading-relaxed">{n.message}</p>}
                      <div className="flex items-center gap-2 mt-1.5 text-[10px] text-gray-400">
                        {n.college_name && <span>{n.college_name}</span>}
                        {n.college_name && <span>·</span>}
                        <span>{timeAgo(n.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
